import { DatePicker, Input, Row, Col, Button } from 'antd';
import { Dispatch, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDictionaryHistory } from '../../api';
import { getLocalTime } from '../../lib';
import {
  UniqueKey,
  IHistoryValue,
  CommonStatusStep,
} from '../../model/api-modules';
import { SystemMenuId } from '../../model/system/menu';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../store/modules/common';
import moment from 'moment';
import { DeployArea, ICommonSupportPageProps } from '../../model';
import { getConfig } from '../../config';
import getFormatMessage from '../../locales/get-format-message';
import { AutoAntTable } from '../../components/auto-ant-table';
import { wrapRequestArea } from '../../lib/http/lib';
import { IRootState } from '../../store/reducers';

export const AdminMailRecord = (props: ICommonSupportPageProps) => {
  const { site } = props;
  const menuDispatch = useDispatch<Dispatch<CommonAction>>();
  const [dictionaryList, setDictionaryList] = useState<IHistoryValue[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const deployArea: DeployArea = getConfig()?.deployArea;
  const isWoa = deployArea === DeployArea.woa;

  const [filteredData, setFilteredData] = useState<IHistoryValue[]>([]);
  const [emailFilter, setEmailFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<string[]>([]);
  const [isDateRangeSelected, setIsDateRangeSelected] =
    useState<boolean>(false);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.MailRecord,
  });

  const isJSON = (value: any) => {
    try {
      JSON.parse(value);
      return true;
    } catch (e) {
      return false;
    }
  };
  const getJsonToParse = (value: any) => {
    if (isJSON(value)) {
      return JSON.parse(value);
    }
    return value;
  };

  // 获取历史记录列表
  const getHistroylList = async (key: UniqueKey) => {
    setLoading(true);
    const res = await wrapRequestArea(() => getDictionaryHistory(key), site);
    if (res) {
      const data = getJsonToParse(res[key]?.HistoryValue);
      setDictionaryList(
        data.sort((a: any, b: any) => {
          const dateA = moment(a.ModifyTime);
          const dateB = moment(b.ModifyTime);
          return dateB.diff(dateA);
        }) || []
      );
      setFilteredData(
        data.sort((a: any, b: any) => {
          const dateA = moment(a.ModifyTime);
          const dateB = moment(b.ModifyTime);
          return dateB.diff(dateA);
        }) || []
      );
    }
    setLoading(false);
  };

  const handleDateRangeChange = (dates: any, dateStrings: string[]) => {
    if (!dates || dates.length === 0) {
      setDateRange(['', '']);
      setIsDateRangeSelected(false);
    } else {
      setDateRange(dateStrings);
      setIsDateRangeSelected(true);
    }
  };
  const handleEmailFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailFilter(e.target.value);
  };
  // 清空重置筛选
  const handleReset = () => {
    setEmailFilter('');
    setDateRange(['', '']);
    setIsDateRangeSelected(false);
  };

  // 筛选
  const handleFilter = () => {
    const filtered = dictionaryList.filter((item) => {
      const value = getJsonToParse(item.Value);
      const emailMatch = !emailFilter || value.Email?.includes(emailFilter);
      const startDate = moment(dateRange[0], 'YYYY-MM-DD');
      const endDate = moment(dateRange[1], 'YYYY-MM-DD');
      const modifyTime = moment(item.ModifyTime, 'YYYY-MM-DD');
      const dateRangeMatch =
        !isDateRangeSelected ||
        modifyTime.isBetween(startDate, endDate, null, '[]');

      return emailMatch && dateRangeMatch;
    });
    setFilteredData(filtered);
  };

  useEffect(() => {
    getHistroylList(UniqueKey.ContactUs);
  }, []);
  useEffect(() => {
    handleFilter();
  }, [emailFilter, dateRange]);

  const woaList = [
    { value: 'email_push', label: getFormatMessage('email_push') },
    { value: 'oa_portal_ad', label: getFormatMessage('oa_portal_ad') },
    { value: 'km', label: getFormatMessage('k_m') },
    { value: 'weibao', label: getFormatMessage('wei_bao') },
    { value: 'qq_tv', label: getFormatMessage('qq_tv') },
    { value: 'gcloud_oa', label: getFormatMessage('gcloud_oa') },
    { value: 'pro_pre', label: getFormatMessage('product_presentation') },
    { value: 'co_rec', label: getFormatMessage('colleague_recommend') },
    { value: 'others', label: getFormatMessage('others') },
  ];
  const cnList = [
    { value: 'gcloud_intl', label: getFormatMessage('gcloud_intl') },
    { value: 'gcloud_cn', label: getFormatMessage('gcloud_chinese') },
    { value: 'so_media', label: getFormatMessage('social_media') },
    {
      value: 'fre_or_co_rec',
      label: getFormatMessage('friend_or_colleague_recommend'),
    },
    { value: 'others', label: getFormatMessage('others') },
  ];
  const intlList = [
    { value: 'gcloud_intl', label: getFormatMessage('intl_gcloud_intl') },
    { value: 'twitter', label: getFormatMessage('twitter') },
    { value: 'facebook', label: getFormatMessage('facebook') },
    { value: 'instagram', label: getFormatMessage('instagram') },
    { value: 'youtube', label: getFormatMessage('youtube') },
    { value: 'gdc', label: getFormatMessage('gdc') },
    { value: 'friends', label: getFormatMessage('friends') },
    { value: 'others', label: getFormatMessage('intl_others') },
  ];

  const contactColumns = [
    {
      title: getFormatMessage('mail_name'),
      dataIndex: 'Value',
      key: 'Name',
      render: (record: string) => {
        const value = getJsonToParse(record);
        if (isWoa) {
          const emailArr = value.Email?.split('@') || [''];
          return <span>{emailArr?.[0]}</span>;
        }
        const firstName = value.FirstName?.split('|')[0] || '';
        return <span>{firstName + value.LastName}</span>;
      },
    },
    {
      title: getFormatMessage('mail_email'),
      dataIndex: 'Value',
      key: 'Email',
      render: (record: string) => {
        const value = getJsonToParse(record);
        return <span>{value.Email}</span>;
      },
    },
    {
      title: getFormatMessage(isWoa ? 'mail_department' : 'mail_company'),
      dataIndex: 'Value',
      key: 'Department',
      render: (record: string) => {
        const value = getJsonToParse(record);
        return <span>{value.Company}</span>;
      },
    },
    {
      title: getFormatMessage('mail_job_title'),
      dataIndex: 'Value',
      key: 'JobTitle',
      render: (record: string) => {
        const value = getJsonToParse(record);
        return <span>{value.JobTitle}</span>;
      },
    },
    {
      title: getFormatMessage('mail_status'),
      dataIndex: 'Status',
      key: 'Status',
      render: (text: number) => {
        return (
          <span>
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                background:
                  text === CommonStatusStep.Reject ? 'red' : '#00ff7f',
                borderRadius: '5px',
                marginRight: '4px',
              }}
            ></span>
            {text === CommonStatusStep.Reject ? 'Error' : 'Success'}
          </span>
        );
      },
    },
    {
      title: getFormatMessage('mail_channel'),
      dataIndex: 'Value',
      key: 'AboutUs',
      render: (record: string) => {
        const value = getJsonToParse(record);
        let channelList = woaList;
        if (deployArea === DeployArea.cn) {
          channelList = cnList;
        } else if (deployArea === DeployArea.singapore) {
          channelList = intlList;
        }
        let channel = '';
        const firstName = getJsonToParse(value.FirstName);
        // woa渠道数据是数组json,cn跟intl是名字首字母|拼接渠道字符串,不直接判断环境是因为兼容测试有不同环境的数据
        if (Array.isArray(firstName)) {
          firstName.length !== 0 &&
            firstName.forEach((item: string, index: number) => {
              const itemName = channelList.find((it) => it.value === item);
              if (itemName) {
                if (index === 0) {
                  channel += itemName.label;
                } else {
                  channel += `, ${itemName.label}`;
                }
              }
            });
        } else if (typeof firstName === 'string') {
          const splitArr = firstName.split('|');
          const channelArr = splitArr[1].split(',');
          channelArr.length !== 0 &&
            channelArr.forEach((item: string, index: number) => {
              const itemName = channelList.find((it) => it.value === item);
              if (itemName) {
                if (index === 0) {
                  channel += itemName.label;
                } else {
                  channel += `, ${itemName.label}`;
                }
              }
            });
        }
        return <span>{channel}</span>;
      },
    },
    {
      title: getFormatMessage('mail_application'),
      dataIndex: 'ModifyTime',
      key: 'ModifyTime',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
      // sorter: (a: any, b: any) => {
      //     const dateA = moment(a.ModifyTime);
      //     const dateB = moment(b.ModifyTime);
      //     return dateB.diff(dateA);
      // },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 10, display: 'flex' }}>
        <Row gutter={4} className="search-container">
          <Col span={3}>
            <Input
              placeholder={getFormatMessage('mail_email')}
              value={emailFilter}
              onChange={handleEmailFilterChange}
              style={{ marginRight: 8 }}
              allowClear
            />
          </Col>
          <Col className="date-picker">
            <DatePicker.RangePicker
              value={
                isDateRangeSelected
                  ? [moment(dateRange[0]), moment(dateRange[1])]
                  : null
              }
              onChange={handleDateRangeChange}
            />
          </Col>
          <Button
            className="clear-all-right gc-btn gc-btn-primary btn-admin"
            onClick={handleReset}
          >
            {getFormatMessage('clear_all')}
          </Button>
        </Row>
      </div>
      <AutoAntTable
        loading={loading}
        dataSource={filteredData || []}
        columns={contactColumns}
      />
    </div>
  );
};
